"use client";

import type { FC, MouseEvent } from "react";
import type { HeadingInfo } from "../lib/markdownToHtml";
import { useEffect, useState } from "react";

const debugLog = (message: string, data?: unknown) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[AnchorNav] ${message}`, data || '');
  }
};

interface AnchorNavigationProps {
  headings: HeadingInfo[];
}

const AnchorNavigation: FC<AnchorNavigationProps> = ({ headings }) => {
  // Track header height
  const [headerHeight, setHeaderHeight] = useState<number>(0);

  useEffect(() => {
    // Measure header height
    const header = document.querySelector('header');
    if (header) {
      const headerRect = header.getBoundingClientRect();
      setHeaderHeight(headerRect.height);
      debugLog('Header height:', headerRect.height);
    }
  }, []);

  useEffect(() => {
    debugLog('Current hash:', window.location.hash);
    
    // Debug all heading IDs and their positions
    for (const heading of headings) {
      const element = document.getElementById(heading.id);
      if (element) {
        const rect = element.getBoundingClientRect();
        debugLog(`Heading "${heading.text}":`, {
          id: heading.id,
          top: rect.top,
          offsetTop: element.offsetTop,
          clientHeight: element.clientHeight,
          scrollY: window.scrollY
        });
      } else {
        debugLog(`Element not found for heading: ${heading.id}`);
      }
    }
  }, [headings]);

  const handleAnchorClick = (e: MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    
    if (element) {
      const elementRect = element.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const offsetTop = headerHeight + 20; // Add 20px padding
      
      debugLog('Scroll debug:', {
        elementTop: elementRect.top,
        pageYOffset: window.pageYOffset,
        absoluteElementTop,
        headerHeight,
        targetScroll: absoluteElementTop - offsetTop
      });

      window.scrollTo({
        top: absoluteElementTop - offsetTop,
        behavior: 'smooth'
      });

      // Update URL after scroll
      window.history.pushState(null, '', `#${id}`);
    }
  };

  // Handle initial hash on load
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      const element = document.getElementById(id);
      if (element && headerHeight > 0) {
        const elementRect = element.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const offsetTop = headerHeight + 20;
        
        window.scrollTo({
          top: absoluteElementTop - offsetTop,
          behavior: 'smooth'
        });
      }
    }
  }, [headerHeight]);

  if (headings.length === 0) {
    return null;
  }

  return (
    <nav className="fixed w-64">
      <div className="space-y-2 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
        <h2 className="font-semibold text-lg mb-4">Table of Contents</h2>
        <ul className="space-y-2">
          {headings.map((heading) => (
            <li 
              key={heading.id}
              style={{ 
                marginLeft: `${(heading.level - 1) * 0.5}rem` 
              }}
            >
              <a
                href={`#${heading.id}`}
                onClick={(e) => handleAnchorClick(e, heading.id)}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-sm block w-full transition-colors no-underline"
              >
                {heading.text}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

export default AnchorNavigation;
